import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Calendar, MapPin, Star, ArrowRight, Mail,
  Music, Trophy, Theater, Smile, ShieldCheck, Ticket,
  Send, CheckCircle2, Lock, Zap,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TestimonialForm from "../components/TestimonialForm";
import WelcomePromoBanner from "../components/WelcomePromoBanner";
import HeroTrialButton from "../components/HeroTrialButton";

// =========================================================================
// =========================================================================
// VERIFIED IMAGE URLs — each visually analyzed & confirmed to match its topic
// =========================================================================
const IMG = {
  CONCERT_CROWD:     "https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?auto=compress&cs=tinysrgb&w=1920",
  CONCERT_STAGE:     "https://images.pexels.com/photos/2263436/pexels-photo-2263436.jpeg?auto=compress&cs=tinysrgb&w=1920",
  FESTIVAL_LIGHTS:   "https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg?auto=compress&cs=tinysrgb&w=1920",
  GUITARIST_BW:      "https://images.pexels.com/photos/210922/pexels-photo-210922.jpeg?auto=compress&cs=tinysrgb&w=1920",
  SOCCER_STADIUM:    "https://images.pexels.com/photos/47730/the-ball-stadion-football-the-pitch-47730.jpeg?auto=compress&cs=tinysrgb&w=1920",
  BASKETBALL_ACTION: "https://images.pexels.com/photos/163452/basketball-dunk-blue-game-163452.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // Sports spécifiques (URLs Pexels vérifiées via search metadata)
  HOCKEY_ACTION:     "https://images.pexels.com/photos/38553430/pexels-photo-38553430.jpeg?auto=compress&cs=tinysrgb&w=1920", // Ice hockey match action
  BASEBALL_PLAYER:   "https://images.pexels.com/photos/34100143/pexels-photo-34100143.jpeg?auto=compress&cs=tinysrgb&w=1920", // Baseball athlete with bat
  FOOTBALL_US:       "https://images.pexels.com/photos/2570139/pexels-photo-2570139.jpeg?auto=compress&cs=tinysrgb&w=1920",   // American football
  TENNIS_ACTION:     "https://images.pexels.com/photos/38503146/pexels-photo-38503146.jpeg?auto=compress&cs=tinysrgb&w=1920", // Tennis match on clay court
};

// HERO CAROUSEL DATA — 4 slides, one per category (verified images)
// =========================================================================
const HERO_SLIDES = [
  {
    img: IMG.CONCERT_CROWD,
    titleFr: "Concerts", titleEn: "Concerts",
    descFr: "Achetez et vendez vos billets de concerts en toute sécurité.",
    descEn: "Buy and sell concert tickets with total security.",
  },
  {
    img: IMG.SOCCER_STADIUM,
    titleFr: "Sports", titleEn: "Sports",
    descFr: "Hockey, football, soccer — vivez l'intensité en toute confiance.",
    descEn: "Hockey, football, soccer — experience the intensity with total trust.",
  },
  {
    img: IMG.FESTIVAL_LIGHTS,
    titleFr: "Festivals", titleEn: "Festivals",
    descFr: "Festivals d'été et grandes scènes — sécurisés de bout en bout.",
    descEn: "Summer festivals and big stages — secured end-to-end.",
  },
  {
    img: IMG.CONCERT_STAGE,
    titleFr: "Spectacles & Humour", titleEn: "Shows & Comedy",
    descFr: "Pas de stress, pas de fraude. Juste l'expérience.",
    descEn: "No stress, no fraud. Just the experience.",
  },
];

const TOP_RANKINGS = [
  { name: "Canadiens de Montréal", events: 12, img: IMG.HOCKEY_ACTION },
  { name: "Festival d'été de Québec", events: 28, img: IMG.FESTIVAL_LIGHTS },
  { name: "OSHEAGA", events: 8, img: IMG.CONCERT_CROWD },
  { name: "Alouettes de Montréal", events: 16, img: IMG.FOOTBALL_US },
  { name: "CF Montréal", events: 14, img: IMG.SOCCER_STADIUM },
  { name: "Coldplay", events: 4, img: IMG.GUITARIST_BW },
  { name: "Bruno Mars", events: 6, img: IMG.CONCERT_STAGE },
  { name: "Taylor Swift", events: 9, img: IMG.CONCERT_CROWD },
  { name: "Festival LASSO", events: 3, img: IMG.FESTIVAL_LIGHTS },
  { name: "Île Soniq", events: 5, img: IMG.CONCERT_STAGE },
];

const VENUES = [
  { name: "Centre Bell", img: IMG.HOCKEY_ACTION },
  { name: "Stade Saputo", img: IMG.SOCCER_STADIUM },
  { name: "Place Bell", img: IMG.HOCKEY_ACTION },
  { name: "Vidéotron", img: IMG.HOCKEY_ACTION },
  { name: "MTELUS", img: IMG.CONCERT_STAGE },
  { name: "Théâtre Capitole", img: IMG.GUITARIST_BW },
  { name: "Stade IGA", img: IMG.TENNIS_ACTION },
  { name: "Métropolis", img: IMG.CONCERT_CROWD },
];
const CITIES = ["Montréal", "Québec", "Laval", "Sherbrooke", "Trois-Rivières", "Gatineau", "Saguenay", "Lévis"];

const CATEGORY_DATA = {
  concerts: [
    { name: "Coldplay", events: 4, img: IMG.GUITARIST_BW },
    { name: "OSHEAGA", events: 8, img: IMG.CONCERT_CROWD },
    { name: "Taylor Swift", events: 9, img: IMG.CONCERT_CROWD },
    { name: "Bruno Mars", events: 6, img: IMG.CONCERT_STAGE },
    { name: "FEQ", events: 28, img: IMG.FESTIVAL_LIGHTS },
    { name: "Festival LASSO", events: 3, img: IMG.FESTIVAL_LIGHTS },
    { name: "Île Soniq", events: 5, img: IMG.CONCERT_STAGE },
    { name: "AC/DC", events: 2, img: IMG.GUITARIST_BW },
  ],
  sports: [
    { name: "Canadiens de Montréal", events: 12, img: IMG.HOCKEY_ACTION },
    { name: "Alouettes de Montréal", events: 16, img: IMG.FOOTBALL_US },
    { name: "CF Montréal", events: 14, img: IMG.SOCCER_STADIUM },
    { name: "Omnium Banque Nationale", events: 13, img: IMG.TENNIS_ACTION },
    { name: "Coupe du Monde", events: 8, img: IMG.SOCCER_STADIUM },
    { name: "Blue Jays de Toronto", events: 25, img: IMG.BASEBALL_PLAYER },
    { name: "Alliance de Montréal", events: 11, img: IMG.BASKETBALL_ACTION },
    { name: "Raptors de Toronto", events: 19, img: IMG.BASKETBALL_ACTION },
  ],
  spectacles: [
    { name: "Cirque du Soleil", events: 6, img: IMG.CONCERT_STAGE },
    { name: "Disney sur glace", events: 3, img: IMG.CONCERT_STAGE },
    { name: "Notre-Dame de Paris", events: 5, img: IMG.CONCERT_CROWD },
    { name: "Le Lac des Cygnes", events: 4, img: IMG.CONCERT_STAGE },
  ],
  humour: [
    { name: "Sugar Sammy", events: 7, img: IMG.GUITARIST_BW },
    { name: "Louis-José Houde", events: 9, img: IMG.CONCERT_STAGE },
    { name: "Martin Matte", events: 6, img: IMG.GUITARIST_BW },
    { name: "Korine Côté", events: 5, img: IMG.CONCERT_STAGE },
  ],
};

// =========================================================================
// HERO CAROUSEL
// =========================================================================
const HeroCarousel = ({ language, onCTA }) => {
  const isFr = language === "fr";
  const [idx, setIdx] = useState(0);
  const next = useCallback(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), []);
  const prev = () => setIdx((i) => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);

  useEffect(() => {
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next]);

  const slide = HERO_SLIDES[idx];

  return (
    <section className="relative w-full overflow-hidden bg-gray-900" data-testid="hero-carousel">
      <div className="relative min-h-[560px] sm:min-h-[600px] md:min-h-[640px] lg:min-h-[720px] aspect-auto md:aspect-[21/9] lg:aspect-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="absolute inset-0"
          >
            <img src={slide.img} alt={slide.titleFr} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/60 to-gray-900/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent" />
          </motion.div>
        </AnimatePresence>

        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center py-16 md:py-20">
          <motion.div
            key={`text-${idx}`}
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-2xl text-white"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-300/15 border border-brand-300/30 rounded-full mb-6 backdrop-blur-sm">
              <ShieldCheck className="h-3.5 w-3.5 text-brand-300" />
              <span className="text-xs font-semibold text-brand-200">
                {isFr ? 'Transferts 100% sécurisés' : '100% secure transfers'}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight mb-5">
              {isFr ? slide.titleFr : slide.titleEn}
            </h1>
            <p className="text-base md:text-xl text-gray-200 leading-relaxed mb-10 max-w-xl">
              {isFr ? slide.descFr : slide.descEn}
            </p>
            <button
              onClick={onCTA}
              data-testid="hero-cta-btn"
              className="group inline-flex items-center gap-2 bg-brand-300 hover:bg-brand-400 text-gray-900 font-bold text-base px-8 py-4 rounded-lg transition-all hover:shadow-xl"
            >
              {isFr ? 'Sécurisez votre transfert' : 'Secure your transfer'}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </motion.div>
        </div>

        {/* Arrows */}
        <button onClick={prev} className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-md text-white rounded-full transition-all" aria-label="Previous">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button onClick={next} className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-md text-white rounded-full transition-all" aria-label="Next">
          <ChevronRight className="h-5 w-5" />
        </button>

        {/* Dots */}
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${i === idx ? 'bg-brand-300 w-8' : 'bg-white/40 hover:bg-white/60 w-1.5'}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// =========================================================================
// TOP RANKING
// =========================================================================
const TopRanking = ({ language, onItemClick }) => {
  const [showAll, setShowAll] = useState(false);
  const isFr = language === "fr";
  const items = showAll ? TOP_RANKINGS : TOP_RANKINGS.slice(0, 5);

  return (
    <section className="py-16 md:py-20 bg-white" data-testid="top-ranking">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8 md:mb-10 flex-wrap gap-3">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Top 10</h2>
          <div className="flex gap-2 flex-wrap">
            {[
              { label: 'Top 10', active: true },
              { label: isFr ? 'À venir' : 'Upcoming' },
              { label: isFr ? 'Rabais' : 'Deals' },
              { label: isFr ? 'Préventes' : 'Presales' },
            ].map((tab, i) => (
              <button
                key={i}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                  tab.active ? 'bg-gray-900 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
          {items.map((it, i) => (
            <motion.button
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: (i % 5) * 0.05 }}
              onClick={onItemClick}
              className="group relative bg-gray-50 hover:bg-white border border-gray-200 hover:border-brand-300 hover:shadow-lg rounded-xl p-3 flex items-center gap-3 transition-all text-left"
            >
              <span className="text-2xl font-extrabold text-brand-700 w-7">{i + 1}</span>
              <img src={it.img} alt={it.name} className="w-14 h-14 rounded-lg object-cover" loading="lazy" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm truncate group-hover:text-brand-700">{it.name}</p>
                <p className="text-xs text-gray-500">{it.events} {isFr ? 'événements' : 'events'}</p>
              </div>
            </motion.button>
          ))}
        </div>

        {!showAll && TOP_RANKINGS.length > 5 && (
          <div className="text-center mt-6">
            <button onClick={() => setShowAll(true)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:text-brand-800">
              {isFr ? 'Voir tout le Top 10' : 'See full Top 10'}
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>
        )}
        {showAll && (
          <div className="text-center mt-6">
            <button onClick={() => setShowAll(false)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900">
              {isFr ? 'Réduire' : 'Show less'}
              <ChevronUp className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

// =========================================================================
// CALENDAR CTA
// =========================================================================
const CalendarSection = ({ language, onClick }) => {
  const isFr = language === "fr";
  return (
    <section className="relative py-20 md:py-28 overflow-hidden" data-testid="calendar-section">
      <div className="absolute inset-0">
        <img src="https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg?auto=compress&cs=tinysrgb&w=1920" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-white"
      >
        <Calendar className="h-10 w-10 text-brand-300 mb-5" />
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4">
          {isFr ? "Calendrier des événements" : "Events calendar"}
        </h2>
        <p className="text-base md:text-lg text-gray-200 max-w-2xl mb-8 leading-relaxed">
          {isFr
            ? "Le seul calendrier regroupant TOUS les événements du Québec en un seul endroit. Concerts, festivals, spectacles, sports — trouvez votre prochain événement."
            : "The only calendar gathering ALL Quebec events in one place. Concerts, festivals, shows, sports — find your next event."}
        </p>
        <button onClick={onClick} className="group inline-flex items-center gap-2 bg-brand-300 hover:bg-brand-400 text-gray-900 font-bold px-7 py-3.5 rounded-lg transition-all">
          {isFr ? "Consulter le calendrier" : "View calendar"}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </button>
      </motion.div>
    </section>
  );
};

// =========================================================================
// CATEGORIES — Tabs + Grid
// =========================================================================
const CategoriesSection = ({ language, onItemClick }) => {
  const isFr = language === "fr";
  const [tab, setTab] = useState("concerts");
  const cats = [
    { id: "concerts", label: isFr ? "Concerts" : "Concerts", icon: Music },
    { id: "sports", label: "Sports", icon: Trophy },
    { id: "spectacles", label: isFr ? "Spectacles" : "Shows", icon: Theater },
    { id: "humour", label: isFr ? "Humour" : "Comedy", icon: Smile },
  ];
  const items = CATEGORY_DATA[tab] || [];

  return (
    <section className="py-16 md:py-20 bg-gray-50" data-testid="categories-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-brand-700 mb-2">
              <MapPin className="inline h-3 w-3 mr-1" />
              {isFr ? "Événements près de Montréal" : "Events near Montreal"}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              {cats.find(c => c.id === tab)?.label}
            </h2>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {cats.map((c) => {
              const Icon = c.icon;
              return (
                <button
                  key={c.id}
                  onClick={() => setTab(c.id)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-all whitespace-nowrap ${
                    tab === c.id ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4"
          >
            {items.map((it, i) => (
              <button
                key={i}
                onClick={onItemClick}
                className="group relative overflow-hidden rounded-xl bg-gray-900 transition-all hover:shadow-xl aspect-[3/4]"
              >
                <img src={it.img} alt={it.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3.5 text-left">
                  <p className="text-white font-bold text-sm md:text-base leading-tight mb-0.5 line-clamp-2 group-hover:text-brand-300 transition-colors">
                    {it.name}
                  </p>
                  <p className="text-xs text-gray-300">{it.events} {isFr ? 'événements' : 'events'}</p>
                </div>
              </button>
            ))}
          </motion.div>
        </AnimatePresence>

        <div className="text-center mt-10">
          <button onClick={onItemClick} className="inline-flex items-center gap-2 text-base font-semibold text-brand-700 hover:text-brand-800 transition-colors">
            {isFr ? `Tous les ${cats.find(c => c.id === tab)?.label.toLowerCase()}` : `All ${cats.find(c => c.id === tab)?.label.toLowerCase()}`}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

// =========================================================================
// NEWSLETTER
// =========================================================================
const NewsletterSection = ({ language }) => {
  const isFr = language === "fr";
  const [contact, setContact] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const onSubmit = (e) => {
    e.preventDefault();
    if (!contact) return;
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setContact("");
  };

  return (
    <section className="relative py-20 md:py-24 overflow-hidden bg-gradient-to-br from-brand-700 to-brand-900 text-white" data-testid="newsletter">
      <div className="absolute inset-0 opacity-10">
        <img src={IMG.CONCERT_CROWD} alt="" className="w-full h-full object-cover" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
      >
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4">
          {isFr ? "Offres exclusives, préventes et nouvelles" : "Exclusive offers, presales and news"}
        </h2>
        <p className="text-base md:text-lg text-brand-100 mb-8">
          {isFr
            ? "Ne manquez rien ! Recevez les meilleures offres par courriel ou SMS."
            : "Don't miss anything! Get the best offers by email or SMS."}
        </p>
        <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
          <div className="relative flex-1">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder={isFr ? "Mobile ou courriel" : "Mobile or email"}
              className="w-full pl-11 pr-4 py-3.5 rounded-lg bg-white text-gray-900 placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              required
            />
          </div>
          <button type="submit" className="bg-gray-900 hover:bg-black text-white font-bold px-6 py-3.5 rounded-lg transition-colors whitespace-nowrap inline-flex items-center justify-center gap-1.5">
            {submitted ? (
              <>
                <CheckCircle2 className="h-4 w-4" />
                {isFr ? "Merci !" : "Thanks!"}
              </>
            ) : (
              <>
                {isFr ? "S'abonner" : "Subscribe"}
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </form>
        <p className="text-xs text-brand-100/70 mt-4 max-w-md mx-auto">
          {isFr
            ? "En vous abonnant, vous acceptez de recevoir nos communications. Désabonnement à tout moment."
            : "By subscribing, you agree to receive our communications. Unsubscribe anytime."}
        </p>
      </motion.div>
    </section>
  );
};

// =========================================================================
// VENUES & CITIES
// =========================================================================
const VenuesCitiesSection = ({ language, onClick }) => {
  const isFr = language === "fr";
  const [tab, setTab] = useState("venues");
  return (
    <section className="py-16 md:py-20 bg-white" data-testid="venues-cities">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            {isFr ? "Salles et villes près de Montréal" : "Venues and cities near Montreal"}
          </h2>
          <div className="flex gap-2">
            {[
              { id: "venues", label: isFr ? "Salles" : "Venues" },
              { id: "cities", label: isFr ? "Villes" : "Cities" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-all ${
                  tab === t.id ? 'bg-gray-900 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {tab === "venues" ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {VENUES.map((v, i) => (
              <button key={i} onClick={onClick} className="group flex items-center gap-3 bg-gray-50 hover:bg-white hover:border-brand-300 border border-gray-200 rounded-xl p-3 transition-all hover:shadow-md text-left">
                <img src={v.img} alt={v.name} className="w-12 h-12 rounded-lg object-cover" loading="lazy" />
                <span className="font-semibold text-gray-900 text-sm group-hover:text-brand-700 truncate">{v.name}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {CITIES.map((c, i) => (
              <button key={i} onClick={onClick} className="group flex items-center gap-3 bg-gray-50 hover:bg-white hover:border-brand-300 border border-gray-200 rounded-xl p-3 transition-all hover:shadow-md text-left">
                <div className="w-12 h-12 rounded-lg bg-brand-50 flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-brand-700" />
                </div>
                <span className="font-semibold text-gray-900 text-sm group-hover:text-brand-700 truncate">{c}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// =========================================================================
// SELL TICKETS CTA (billets.ca-style with image)
// =========================================================================
const SellTicketsSection = ({ language, onClick }) => {
  const isFr = language === "fr";
  return (
    <section className="relative py-20 md:py-28 overflow-hidden bg-gray-900 text-white" data-testid="sell-tickets">
      <div className="absolute inset-0 opacity-30">
        <img src={IMG.CONCERT_STAGE} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/85 to-gray-900/50" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-brand-300 mb-3">
              {isFr ? 'Pour les vendeurs' : 'For sellers'}
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-5">
              {isFr ? "Vendez vos billets en toute confiance." : "Sell your tickets with confidence."}
            </h2>
            <p className="text-gray-300 text-base md:text-lg mb-8 leading-relaxed max-w-xl">
              {isFr
                ? "Affichez vos billets gratuitement. Partagez votre lien unique sur les réseaux sociaux. Vous ne transférez vos billets qu'une fois la vente confirmée."
                : "List tickets for free. Share your unique link on social media. Only transfer tickets once the sale is confirmed."}
            </p>
            <button onClick={onClick} className="group inline-flex items-center gap-2 bg-brand-300 hover:bg-brand-400 text-gray-900 font-bold px-7 py-4 rounded-lg transition-all">
              {isFr ? 'Vendre mes billets' : 'Sell my tickets'}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <p className="text-sm text-gray-400 mt-4">
              {isFr ? 'Aucun frais tant que vos billets ne sont pas vendus.' : 'No fees until your tickets sell.'}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: Ticket, label: isFr ? 'Annonce gratuite' : 'Free listing' },
              { icon: ShieldCheck, label: isFr ? 'Paiement sécurisé' : 'Secure payment' },
              { icon: Lock, label: isFr ? 'Anonymat préservé' : 'Privacy first' },
              { icon: Zap, label: isFr ? 'Lien partageable' : 'Shareable link' },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="bg-white/[0.06] border border-white/10 rounded-xl p-5 backdrop-blur-sm hover:bg-white/[0.10] transition-all"
                >
                  <Icon className="h-5 w-5 text-brand-300 mb-3" />
                  <p className="text-sm font-medium text-white">{f.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

// =========================================================================
// REVIEWS — billets.ca-style with 4.9 prominent
// =========================================================================
const ReviewsSection = ({ user, testimonials, loading, t, isFr, fallback }) => {
  return (
    <section className="py-16 md:py-24 bg-gray-50" data-testid="reviews">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-wider text-brand-700 mb-3">
            {isFr ? 'Avis clients' : 'Customer reviews'}
          </p>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <span className="text-3xl font-extrabold text-gray-900">4.9</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-3">
            {isFr ? "Plus de 500 avis vérifiés" : "Over 500 verified reviews"}
          </h2>
          <p className="text-base md:text-lg text-gray-600">
            {isFr
              ? "Voici ce que disent nos acheteurs et vendeurs."
              : "Here's what our buyers and sellers say."}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-400">…</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {(testimonials.length > 0 ? testimonials.slice(0, 3) : fallback).map((tm, i) => (
              <motion.div
                key={tm.id || i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex mb-4 gap-0.5">
                  {[...Array(5)].map((_, idx) => (
                    <Star key={idx} className={`h-4 w-4 ${idx < (tm.rating || 5) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`} />
                  ))}
                </div>
                <p className="text-gray-800 mb-5 leading-relaxed">"{tm.comment}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <div className="w-9 h-9 rounded-full bg-brand-50 flex items-center justify-center text-brand-700 font-bold text-sm">
                    {tm.display_name?.charAt(0) || '·'}
                  </div>
                  <div>
                    <p className="text-gray-900 font-semibold text-sm">{tm.display_name}</p>
                    <p className="text-gray-500 text-xs">
                      {tm.role === 'buyer' ? t('landing.buyerLabel') : t('landing.sellerLabel')}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="text-center mt-10">
          <TestimonialForm
            user={user}
            triggerText={isFr ? 'Laisser un témoignage' : 'Leave a testimonial'}
            triggerVariant="default"
            triggerClassName="bg-white border border-gray-300 hover:border-gray-400 text-gray-900 font-semibold px-6 py-3 rounded-lg"
          />
        </div>
      </div>
    </section>
  );
};

// =========================================================================
// MAIN PAGE
// =========================================================================
const LandingPage = ({ user, onLogout, refreshUserData }) => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const isFr = language === "fr";
  const [testimonials, setTestimonials] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    const fetchT = async () => {
      try {
        const r = await axios.get(`${API}/testimonials?limit=6`);
        setTestimonials(r.data);
      } catch (e) { console.error(e); }
      finally { setLoadingTestimonials(false); }
    };
    fetchT();
  }, [API]);

  const handleGetStarted = () => {
    if (user) {
      if (user.role === "super_admin") navigate("/super-admin");
      else if (user.role === "admin") navigate("/admin");
      else if (user.role === "owner") navigate("/business");
      else navigate("/dashboard");
    } else {
      navigate("/auth");
    }
  };
  const goEvents = () => navigate("/events");
  const goSell = () => navigate(user ? "/dashboard" : "/auth");

  const fallbackTestimonials = [
    { id: 'a', display_name: 'Marc L.', role: 'buyer', rating: 5, comment: t('landing.testimonial1') },
    { id: 'b', display_name: 'Sophie B.', role: 'seller', rating: 5, comment: t('landing.testimonial2') },
    { id: 'c', display_name: 'Alexandre T.', role: 'buyer', rating: 5, comment: t('landing.testimonial3') },
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
      <div className="h-16 lg:h-[68px]" />

      <HeroCarousel language={language} onCTA={handleGetStarted} />

      {/* Hero trial promo button strip (only when active promo) */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-center">
          <HeroTrialButton user={user} />
        </div>
      </div>

      <TopRanking language={language} onItemClick={goEvents} />

      <CalendarSection language={language} onClick={goEvents} />

      <CategoriesSection language={language} onItemClick={goEvents} />

      <NewsletterSection language={language} />

      <VenuesCitiesSection language={language} onClick={goEvents} />

      <SellTicketsSection language={language} onClick={goSell} />

      <ReviewsSection
        user={user}
        testimonials={testimonials}
        loading={loadingTestimonials}
        t={t}
        isFr={isFr}
        fallback={fallbackTestimonials}
      />

      {/* HOW IT WORKS — sober at bottom for those who want to know more */}
      <section id="how-it-works" className="py-20 md:py-24 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-wider text-brand-700 mb-3">
              {isFr ? 'Comment ça fonctionne' : 'How it works'}
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-3">
              {t('landing.howItWorksTitle')}
            </h2>
            <p className="text-base md:text-lg text-gray-600">{t('landing.howItWorksSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              { num: '01', icon: Send, title: t('landing.step1Title'), desc: t('landing.step1Desc') },
              { num: '02', icon: ShieldCheck, title: t('landing.step2Title'), desc: t('landing.step2Desc') },
              { num: '03', icon: CheckCircle2, title: t('landing.step3Title'), desc: t('landing.step3Desc') },
            ].map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="relative bg-white border border-gray-200 hover:border-brand-300 hover:shadow-lg rounded-2xl p-7 transition-all"
                >
                  <span className="absolute top-5 right-6 text-[11px] font-bold text-gray-300">{step.num}</span>
                  <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center mb-5">
                    <Icon className="h-5 w-5 text-brand-700" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">{t('landing.ctaTitle')}</h2>
          <p className="text-base md:text-lg text-gray-600 max-w-xl mx-auto mb-8">{t('landing.ctaSubtitle')}</p>
          <button onClick={handleGetStarted} className="group inline-flex items-center gap-2 bg-gray-900 hover:bg-black text-white font-bold px-8 py-4 rounded-lg transition-all">
            {t('landing.getStartedNow')}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </section>

      <Footer variant="full" />
      <WelcomePromoBanner user={user} />
    </div>
  );
};

export default LandingPage;
